<script lang="ts" setup>
  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'
  import { DateTime } from 'luxon'
  import { useI18n } from 'vue-i18n'
  import { useFeatureFlags } from '@/composables/useFeatureFlag.ts'
  import { useGeneratedServiceRevenueStore } from '@/stores'
  import { ServiceRevenueDefaultReadSync } from '@/api'


  const isLoading = ref<boolean>(true)
  const titrop = useTitrop()
  const featureFlag = useFeatureFlags()
  const { t } = useI18n()

  const revenueStore = useGeneratedServiceRevenueStore()
  const lastRevenueEntry = ref<ServiceRevenueDefaultReadSync>()


  onMounted(async () => {
    lastRevenueEntry.value = await revenueStore.findAll(
      {
        query: {
          itemsPerPage: 1,
          'order[refDateTime]': 'desc',
          'refDateTime[after]': DateTime.now().startOf('day').toISO({ suppressMilliseconds: true })
        }
      }, { forceRefresh: true }
    ).then(d => d.value?.[0])
    isLoading.value = false
  })
</script>

<template>
  <div class="max-w-full @container/WidgetServiceRevenue">
    <div v-if="titrop.user.roles.department_leader && featureFlag.isReady && featureFlag.isEnabled('widget_service_revenue')">
      <WidgetSkeleton v-if="isLoading" variant="service-revenue" />
      <div v-else>
        <div class="w-full">
          <p class="text-sm text-gray-600">{{ $formatter.MonthNameByDate(lastRevenueEntry?.refDateTime) }} {{ DateTime.fromISO(lastRevenueEntry?.refDateTime!).year }}</p>
          <div class="flex md:flex-row justify-between md:items-start mb-2">
            <div>
              <p class="text-2xl font-semibold text-gray-900">{{ lastRevenueEntry?.revenue?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }} EUR</p>
              <p class="text-xs text-gray-500">{{ t('target', { value: lastRevenueEntry?.targetRevenue?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }) }}</p>
            </div>

            <div class="text-right">
              <p class="text-2xl font-semibold text-gray-900">{{ t('hours', { value: lastRevenueEntry?.hours?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }) }}</p>
              <p class="text-xs text-gray-500">{{ t('target', { value: lastRevenueEntry?.targetHours?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }) }}</p>
            </div>
          </div>

          <p class="text-sm mt-4 text-gray-600">{{ $formatter.MonthNameByDate(DateTime.fromISO(lastRevenueEntry?.refDateTime!).minus({ month: 1 })) }} {{ DateTime.fromISO(lastRevenueEntry?.refDateTime!).minus({ month: 1 }).year }}</p>
          <div class="flex justify-between items-start text-gray-400">
            <div>
              <p class="text-xl font-semibold">{{ lastRevenueEntry?.prevMonthRevenue?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }} EUR</p>
            </div>

            <div class="text-right">
              <p class="text-xl font-semibold">{{ t('hours', { value: lastRevenueEntry?.prevMonthHours?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }) }}</p>
            </div>
          </div>

          <div class="text-xs text-gray-500 mt-1 border-t border-gray-100 pt-1">
            {{ t('asOf', { value: $formatter.DateTime(lastRevenueEntry?.refDateTime!) }) }}
          </div>
        </div>
      </div>
    </div>
    <div v-else class="w-full">
      <Message>
        <span v-if="!featureFlag.isEnabled('widget_service_revenue')">{{ t('disabled') }}</span>
        <span v-else-if="!titrop.user.roles.department_leader">{{ t('leaderOnly') }}</span>
      </Message>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "current": "Aktuell:",
    "target": "Ziel: {value}",
    "hours": "{value} Std.",
    "perMonth": "ø {value} EUR/Monat",
    "asOf": "Stand: {value}",
    "disabled": "Dieses Widget ist vorübergehend deaktiviert.",
    "leaderOnly": "Der Inhalt ist nur für Benutzer mit der Rolle \"Abteilungsleiter\" verfügbar."
  },
  "en": {
    "current": "Current:",
    "target": "Target: {value}",
    "hours": "{value} hrs",
    "perMonth": "Avg. {value} EUR/month",
    "asOf": "As of: {value}",
    "disabled": "This widget is temporarily disabled.",
    "leaderOnly": "This content is only available to users with the department leader role."
  }
}
</i18n>
